import type { WebwinImage } from "../../engine/ImageStore.js";
import type { TooltipContent } from "../render/RenderContext.js";

/**
 * One row of a menu, as data rather than a control.
 *
 * Menus are described declaratively and the popup builds itself from the
 * description, so an application never assembles a control tree by hand and
 * never has to tear one down.
 */
export interface MenuItemSpec {
  /**
   * Row caption. An `&` marks the mnemonic — "&File" underlines the F and
   * makes Alt+F open it. Use `&&` for a literal ampersand.
   */
  text?: string;
  /**
   * Said when the pointer rests on the row - what the command does, where the
   * caption only names it. Shown for a disabled row too: "why is this gray" is
   * the question a tooltip on a menu most often answers.
   */
  tooltip?: TooltipContent;
  /** Right-aligned accelerator hint such as "Ctrl+S". Display only. */
  shortcut?: string;
  /** Draw a tick (or a bullet, with `radio`) in the check column. */
  checked?: boolean;
  /** Check as a radio bullet rather than a tick. */
  radio?: boolean;
  /** Optional icon, drawn in the check column when unchecked. */
  icon?: WebwinImage | null;
  enabled?: boolean;
  /** A horizontal rule. Every other field is ignored. */
  separator?: boolean;
  /** Child rows; a non-empty list turns this row into a submenu. */
  items?: MenuItemSpec[];
  onClick?: (item: MenuItemSpec) => void;
  /** Arbitrary payload for the application. */
  tag?: unknown;
}

/** A separator row, for readability at call sites. */
export const menuSeparator: MenuItemSpec = { separator: true };

export function isSeparator(item: MenuItemSpec): boolean {
  return item.separator === true;
}

export function hasSubmenu(item: MenuItemSpec): boolean {
  return (item.items?.length ?? 0) > 0;
}

export function isItemEnabled(item: MenuItemSpec): boolean {
  return !isSeparator(item) && item.enabled !== false;
}

/** Rows that can take the highlight: enabled, and not a separator. */
export function isSelectable(item: MenuItemSpec): boolean {
  return isItemEnabled(item);
}

export interface ParsedLabel {
  /** The caption with mnemonic markers removed. */
  text: string;
  /** Index into `text` of the mnemonic character, or -1. */
  mnemonicIndex: number;
  /** Lower-cased mnemonic character, or "". */
  mnemonic: string;
}

/**
 * Split "&Save As" into its display text and the mnemonic it marks.
 *
 * `&&` is an escaped ampersand; a trailing lone `&` is ignored rather than
 * treated as marking the terminator.
 */
export function parseMnemonic(label: string): ParsedLabel {
  let text = "";
  let mnemonicIndex = -1;
  for (let i = 0; i < label.length; i++) {
    const ch = label[i]!;
    if (ch !== "&") {
      text += ch;
      continue;
    }
    const next = label[i + 1];
    if (next === undefined) break; // trailing "&" is not a marker
    if (next === "&") {
      text += "&";
      i++;
      continue;
    }
    if (mnemonicIndex < 0) mnemonicIndex = text.length;
  }
  return {
    text,
    mnemonicIndex,
    mnemonic: mnemonicIndex >= 0 ? text[mnemonicIndex]!.toLowerCase() : "",
  };
}
